import { AICoachGoalDecisionType, AICoachGoalType } from 'enums/AICoach';

import Api, { FetchMethodType } from './Api';
import { AICoachGoalItem } from './LoanOfferApi';

export interface GetAICoachGoalsResponse {
  goals: AICoachGoalItem[];
  plaidConnected?: boolean;
}

export interface GoalProgress {
  goalType: AICoachGoalType;
  decisionType?: AICoachGoalDecisionType;
  description: string;
  minValue: number;
  maxValue: number;
  currentValue: number;
  previousValue: number | null;
  lastUpdateDate: string | null;
  isCompleted: boolean;
}

export interface NextStep {
  title: string;
  description: string;
}

export interface GetNextStepsResponse {
  nextSteps: NextStep[];
}

export interface ParaphraseNextStepResponse {
  paraphrasedText: string;
}

export enum UpdateGoalsMode {
  Merge = 'merge',
  Replace = 'replace',
}

export interface UpdateAICoachGoalsParams {
  applicationId: string;
  goals: AICoachGoalItem[];
  mode?: UpdateGoalsMode;
}

export interface StartScoreImprovementPlanResponse {
  goal: AICoachGoalItem;
}

export interface AICoachGoalsApi {
  getAICoachGoals(applicationId: string): Promise<GetAICoachGoalsResponse>;
  createAICoachGoals(applicationId: string, goals: AICoachGoalItem[]): Promise<GetAICoachGoalsResponse>;
  updateAICoachGoals(params: UpdateAICoachGoalsParams): Promise<GetAICoachGoalsResponse>;
  deleteAICoachGoals(applicationId: string): Promise<void>;
  updateGoalProgress(userId: string): Promise<GoalProgress | null>;
  startScoreImprovementPlan(applicationId: string): Promise<StartScoreImprovementPlanResponse>;
  getNextSteps(userId: string): Promise<GetNextStepsResponse>;
  paraphraseNextStep(
    applicationId: string,
    recommendedPrimaryGoal: AICoachGoalDecisionType,
  ): Promise<ParaphraseNextStepResponse>;
}

export default class AICoachGoalsRestApi extends Api implements AICoachGoalsApi {
  public async getAICoachGoals(applicationId: string): Promise<GetAICoachGoalsResponse> {
    const response = await this.fetch<GetAICoachGoalsResponse>(`/aiCoachGoal/${applicationId}`, {
      method: FetchMethodType.GET,
    });
    return response;
  }

  public async createAICoachGoals(applicationId: string, goals: AICoachGoalItem[]): Promise<GetAICoachGoalsResponse> {
    return this.fetch<GetAICoachGoalsResponse>(`/aiCoachGoal`, {
      method: FetchMethodType.POST,
      body: { applicationId, goals },
    });
  }

  public async updateAICoachGoals(params: UpdateAICoachGoalsParams): Promise<GetAICoachGoalsResponse> {
    return this.fetch<GetAICoachGoalsResponse>(`/aiCoachGoal`, {
      method: FetchMethodType.PATCH,
      body: params,
    });
  }

  public async deleteAICoachGoals(applicationId: string): Promise<void> {
    return this.fetch<void>(`/aiCoachGoal`, {
      method: FetchMethodType.DELETE,
      body: { applicationId },
    });
  }

  public async updateGoalProgress(userId: string): Promise<GoalProgress | null> {
    return this.fetch<GoalProgress | null>(`/aiCoachGoal/progress/${userId}`, {
      method: FetchMethodType.PUT,
    });
  }

  public async startScoreImprovementPlan(applicationId: string): Promise<StartScoreImprovementPlanResponse> {
    return this.fetch<StartScoreImprovementPlanResponse>(`/aiCoachGoal/startScoreImprovementPlan/${applicationId}`, {
      method: FetchMethodType.POST,
    });
  }

  public async getNextSteps(userId: string): Promise<GetNextStepsResponse> {
    return this.fetch<GetNextStepsResponse>(`/aiCoachGoal/nextSteps/${userId}`, {
      method: FetchMethodType.GET,
    });
  }

  public async paraphraseNextStep(
    applicationId: string,
    recommendedPrimaryGoal: AICoachGoalDecisionType,
  ): Promise<ParaphraseNextStepResponse> {
    return this.fetch<ParaphraseNextStepResponse>(`/aiCoachGoal/paraphraseNextStep/${applicationId}`, {
      method: FetchMethodType.POST,
      body: { recommendedPrimaryGoal },
    });
  }
}
